import { BsWhatsapp } from "react-icons/bs";
import { MutableRefObject } from "react";
import { Popover } from "@headlessui/react";
import { CheckCircleIcon, LockClosedIcon } from "@heroicons/react/24/outline";

interface CartUserPinCardProps {
  cartUserPinDigits: string[];
  cartPinInputRefs: MutableRefObject<Array<HTMLInputElement | null>>;
  isCartPinComplete: boolean;
  isCartPinVerifying: boolean;
  isCartPinCleared: boolean;
  onCartPinChange: (index: number, value: string) => void;
  onCartPinKeyDown: (index: number, key: string) => void;
  onVerifyCartPin: () => void;
  onSendCartPin: () => void;
  buttonClassName: string;
}

const CartUserPinCard = ({
  cartUserPinDigits,
  cartPinInputRefs,
  isCartPinComplete,
  isCartPinVerifying,
  isCartPinCleared,
  onCartPinChange,
  onCartPinKeyDown,
  onVerifyCartPin,
  onSendCartPin,
  buttonClassName,
}: CartUserPinCardProps) => (
  <Popover className="relative">
    <Popover.Button
      className={`${buttonClassName} ${
        isCartPinCleared
          ? "border-green-200 bg-green-50 text-[#07863d] hover:bg-green-100"
          : "border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
      }`}
    >
      {isCartPinCleared ? <CheckCircleIcon className="h-4 w-4" /> : <LockClosedIcon className="h-4 w-4" />}
      User PIN
    </Popover.Button>
    <Popover.Panel className="absolute right-0 z-40 mt-2 w-[260px] rounded-xl border border-gray-100 bg-white p-4 shadow-lg">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[14px] font-semibold text-[#000000]">User Pin</p>
          <span className="text-[11px] font-medium text-[#6D717F]">4-digit PIN</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {cartUserPinDigits.map((digit, index) => (
            <input
              key={`cart-user-pin-${index}`}
              ref={(element) => {
                cartPinInputRefs.current[index] = element;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(event) => onCartPinChange(index, event.target.value)}
              onKeyDown={(event) => onCartPinKeyDown(index, event.key)}
              className="h-10 w-full rounded-[8px] border border-[#D9DEE7] bg-white text-center text-[16px] font-semibold text-[#111111] outline-none focus:border-[#111111]"
            />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onVerifyCartPin}
            disabled={!isCartPinComplete || isCartPinVerifying}
            className={`h-[40px] w-full rounded-[8px] px-3 text-[12px] font-semibold transition-all ${
              isCartPinComplete && !isCartPinVerifying
                ? "bg-[#111111] text-white hover:opacity-95"
                : "cursor-not-allowed bg-[#B9B9B9] text-[#F3F3F3]"
            }`}
          >
            {isCartPinVerifying ? "Verifying..." : "Verify"}
          </button>
          <button
            type="button"
            onClick={onSendCartPin}
            className="h-[40px] w-full rounded-[8px] border border-[#25D366] bg-white px-3 text-[12px] font-semibold text-[#25D366] transition-colors hover:bg-[#E8F5E9] flex items-center gap-2 justify-center"
          >
            Send Via
            <BsWhatsapp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Popover.Panel>
  </Popover>
);

export default CartUserPinCard;
